import type { EntitlementKey } from "@/hooks/usePlanEntitlements";

export type ScreenGroup = "Main" | "Management" | "CRM" | "Analytics & Settings";

/** Entitlements that go when CRM is switched off for the outlet (the loyalty points program stays). */
export const CRM_ENTITLEMENTS: ReadonlySet<EntitlementKey> = new Set<EntitlementKey>([
  "crm",
  "basicCrm",
  "fullCrm",
  "offers",
]);

export interface ScreenCatalogEntry {
  id: string;
  path: string;
  title: string;
  group: ScreenGroup;
  sidebar: boolean;
  anyOf: string[];
  feature?: EntitlementKey;
}

/**
 * Screen → permission catalog.
 *
 * Array order among `sidebar: true` entries is load-bearing for
 * `firstAllowedPath`: Dashboard before Orders; Orders before Reservations,
 * Tables, Kitchen, and Menu; Inventory immediately before Menu so Inventory
 * Manager lands on `/inventory` (they also have `menu.view`).
 */
export const SCREEN_CATALOG: ScreenCatalogEntry[] = [
  {
    id: "dashboard",
    path: "/dashboard",
    title: "Dashboard",
    group: "Main",
    sidebar: true,
    anyOf: ["dashboard.view"],
  },
  {
    id: "orders",
    path: "/orders",
    title: "Orders",
    group: "Main",
    sidebar: true,
    anyOf: ["orders.view"],
  },
  {
    id: "reservations",
    path: "/reservations",
    title: "Reservations",
    group: "Main",
    sidebar: true,
    anyOf: ["tables.view"],
  },
  {
    id: "tables",
    path: "/tables",
    title: "Tables",
    group: "Main",
    sidebar: true,
    anyOf: ["tables.view"],
  },
  {
    id: "kitchen",
    path: "/kitchen",
    title: "Kitchen Display",
    group: "Main",
    sidebar: true,
    anyOf: ["kot.view"],
  },
  {
    id: "inventory",
    path: "/inventory",
    title: "Inventory",
    group: "Main",
    sidebar: true,
    anyOf: ["inventory.view"],
  },
  {
    id: "menu",
    path: "/menu",
    title: "Menu",
    group: "Main",
    sidebar: true,
    anyOf: ["menu.view"],
  },
  {
    id: "qr-menu",
    path: "/qr-menu",
    title: "QR Menu",
    group: "Main",
    sidebar: true,
    anyOf: ["qr_menus.manage"],
  },
  {
    id: "history",
    path: "/history",
    title: "History",
    group: "Management",
    sidebar: true,
    anyOf: ["history.view"],
  },
  {
    id: "staff",
    path: "/staff",
    title: "Staff",
    group: "Management",
    sidebar: true,
    anyOf: ["staff.manage"],
  },
  {
    id: "outlets",
    path: "/outlets",
    title: "Outlets",
    group: "Management",
    sidebar: true,
    anyOf: ["staff.manage"],
  },
  {
    id: "crm",
    path: "/crm",
    title: "Dashboard",
    group: "CRM",
    sidebar: true,
    anyOf: ["crm.manage"],
    feature: "basicCrm",
  },
  {
    id: "crm-customers",
    path: "/crm/customers",
    title: "Customers",
    group: "CRM",
    sidebar: true,
    anyOf: ["crm.manage"],
    feature: "basicCrm",
  },
  {
    id: "crm-inbox",
    path: "/crm/inbox",
    title: "Inbox",
    group: "CRM",
    sidebar: true,
    anyOf: ["crm.manage"],
    feature: "fullCrm",
  },
  {
    id: "crm-campaigns",
    path: "/crm/campaigns",
    title: "Campaigns",
    group: "CRM",
    sidebar: true,
    anyOf: ["crm.manage"],
    feature: "fullCrm",
  },
  {
    id: "loyalty-automations",
    path: "/loyalty-automations",
    title: "Automations",
    group: "CRM",
    sidebar: true,
    anyOf: ["discounts.manage"],
    feature: "offers",
  },
  {
    id: "crm-segments",
    path: "/crm/segments",
    title: "Segments",
    group: "CRM",
    sidebar: true,
    anyOf: ["crm.manage"],
    feature: "fullCrm",
  },
  {
    id: "crm-rules",
    path: "/crm/rules",
    title: "Rules",
    group: "CRM",
    sidebar: true,
    anyOf: ["crm.manage"],
    feature: "fullCrm",
  },
  {
    id: "settings-loyalty",
    path: "/settings/loyalty",
    title: "Loyalty Program",
    group: "Analytics & Settings",
    sidebar: false,
    anyOf: ["discounts.manage"],
    feature: "loyalty",
  },
  {
    id: "settings-discounts",
    path: "/settings/discounts",
    title: "Discount Campaigns",
    group: "Analytics & Settings",
    sidebar: false,
    anyOf: ["discounts.manage"],
  },
  {
    id: "feedback",
    path: "/feedback",
    title: "Feedback Forms",
    group: "CRM",
    sidebar: true,
    anyOf: ["feedback.view"],
    feature: "crm",
  },
  {
    id: "crm-feedback",
    path: "/crm/feedback",
    title: "Reviews & Reputation",
    group: "CRM",
    sidebar: true,
    anyOf: ["crm.manage"],
    feature: "fullCrm",
  },
  {
    id: "reports",
    path: "/reports",
    title: "Reports",
    group: "Analytics & Settings",
    sidebar: true,
    anyOf: ["reports.view"],
  },
  {
    id: "settings",
    path: "/settings",
    title: "Settings",
    group: "Analytics & Settings",
    sidebar: true,
    anyOf: ["settings.view", "business_settings.manage"],
  },
  {
    id: "settings-time-machine",
    path: "/settings/time-machine",
    title: "Time Machine",
    group: "Analytics & Settings",
    sidebar: false,
    anyOf: ["settings.view", "business_settings.manage"],
  },
  {
    id: "downloads",
    path: "/downloads",
    title: "Downloads",
    group: "Analytics & Settings",
    sidebar: false,
    anyOf: ["settings.view"],
  },
  {
    id: "settings-account",
    path: "/settings/account",
    title: "Account",
    group: "Analytics & Settings",
    sidebar: false,
    anyOf: ["billing.manage"],
  },
  {
    id: "crm-customer-detail",
    path: "/crm/customers/:customerId",
    title: "Customer",
    group: "CRM",
    sidebar: false,
    anyOf: ["crm.manage"],
    feature: "basicCrm",
  },
  {
    id: "crm-campaign-new",
    path: "/crm/campaigns/new",
    title: "New Campaign",
    group: "CRM",
    sidebar: false,
    anyOf: ["crm.manage"],
    feature: "fullCrm",
  },
  {
    id: "crm-campaign-detail",
    path: "/crm/campaigns/:id",
    title: "Campaign",
    group: "CRM",
    sidebar: false,
    anyOf: ["crm.manage"],
    feature: "fullCrm",
  },
  {
    id: "menu-detail",
    path: "/menu/menus/:variantId",
    title: "Menu Detail",
    group: "Main",
    sidebar: false,
    anyOf: ["menu.view"],
  },
  {
    id: "feedback-order",
    path: "/feedback/:orderId",
    title: "Order Feedback",
    group: "CRM",
    sidebar: false,
    anyOf: ["feedback.view"],
    feature: "crm",
  },
];

function pathMatches(entryPath: string, path: string): boolean {
  if (entryPath === path) return true;
  const paramIndex = entryPath.indexOf("/:");
  if (paramIndex === -1) return false;
  const prefix = entryPath.slice(0, paramIndex);
  if (!path.startsWith(prefix + "/") && path !== prefix) return false;
  const entrySegments = entryPath.split("/");
  const pathSegments = path.split("/");
  if (entrySegments.length !== pathSegments.length) return false;
  return entrySegments.every((segment, i) => segment.startsWith(":") || segment === pathSegments[i]);
}

/** Exact path, then parameterized templates, then longest prefix. */
export function anyOfForPath(path: string): string[] | undefined {
  const exact = SCREEN_CATALOG.find((screen) => screen.path === path);
  if (exact) return exact.anyOf;

  const parameterized = SCREEN_CATALOG.find((screen) => pathMatches(screen.path, path));
  if (parameterized) return parameterized.anyOf;

  let best: ScreenCatalogEntry | undefined;
  for (const screen of SCREEN_CATALOG) {
    if (screen.path.includes("/:")) continue;
    if (path.startsWith(screen.path + "/") && (!best || screen.path.length > best.path.length)) {
      best = screen;
    }
  }
  return best?.anyOf;
}

/** The entitlement a catalog screen needs (exact catalog path, e.g. "/crm/customers/:customerId"). */
export function featureForPath(path: string): EntitlementKey | undefined {
  return SCREEN_CATALOG.find((screen) => screen.path === path)?.feature;
}

export function sidebarPathsFor(permissions: string[]): string[] {
  return SCREEN_CATALOG.filter(
    (s) => s.sidebar && s.anyOf.some((p) => permissions.includes(p)),
  ).map((s) => s.path);
}
